/**
 * ThemeService - Theme Loading and Switching
 *
 * This service manages the unified theme system for IntelliFire EasyMap3.
 * - Loads core.css (always required)
 * - Loads user's selected theme from localStorage
 * - Provides methods to switch themes dynamically
 * - Persists theme selection across sessions
 *
 * Usage:
 *   // In main.tsx (before React renders)
 *   await ThemeService.initialize();
 *
 *   // To switch themes (from UI)
 *   await ThemeService.loadTheme('summer');
 */

import { logger } from '../utils/logger';
import { setCookie, getRootDomain } from '../utils/cookieUtils';

const log = logger('ThemeService');

export interface ThemeMetadata {
  id: string;
  name: string;
  description: string;
  author: string;
  category: 'base' | 'seasonal' | 'accessibility' | 'variant' | 'custom';
  filename: string;
  colorPreview: Array<{
    name: string;
    value: string;
  }>;
}

export interface ThemeManifest {
  version: string;
  generated: string;
  themes: ThemeMetadata[];
}

export class ThemeService {
  private static readonly THEME_BASE = 'em3-selected-theme';
  private static readonly THEME_COOKIE_NAME = 'em3_theme'; // Cookie for cross-app sharing (CMS reads it)

  /**
   * User-scoped localStorage key. Returns null when no userId is set
   * in sessionStorage (em3:bootUserId is written by main.tsx pre-fetch).
   * Per-user key prevents shared workstations from leaking theme between users.
   */
  private static getThemeKey(): string | null {
    const uid = sessionStorage.getItem('em3:bootUserId');
    return uid ? `${this.THEME_BASE}:${uid}` : null;
  }
  private static readonly DEFAULT_THEME = 'light';
  private static readonly CORE_CSS_ID = 'em3-core-css';
  private static readonly THEME_CSS_ID = 'em3-active-theme';

  private static themesManifest: ThemeManifest | null = null;
  private static initialized = false;

  /**
   * Initialize the theme system
   * Call this once during application startup, before React renders
   */
  static async initialize(): Promise<void> {
    if (this.initialized) {
      log.warn('ThemeService already initialized');
      return;
    }

    try {
      log.debug(' Initializing theme system...');

      // Load core CSS first (always required)
      await this.loadCoreCSS();

      // Load themes manifest
      await this.loadThemesManifest();

      // Load user's selected theme (or default)
      const selectedTheme = this.getSelectedTheme();
      await this.loadTheme(selectedTheme, false); // false = don't save (already in localStorage)

      this.initialized = true;
      log.debug(` Theme system initialized with theme: ${selectedTheme}`);
    } catch (error) {
      log.error(' Failed to initialize theme system:', error);
      // Load default theme as fallback
      await this.loadTheme(this.DEFAULT_THEME, false);
      this.initialized = true;
    }
  }

  /**
   * Load core CSS variables (always required)
   */
  private static async loadCoreCSS(): Promise<void> {
    // Check if already loaded
    if (document.getElementById(this.CORE_CSS_ID)) {
      log.debug(' Core CSS already loaded');
      return;
    }

    const corePath = '/themes/core.css';
    await this.loadCSS(corePath, this.CORE_CSS_ID);
    log.debug(' Core CSS loaded');
  }

  /**
   * Load themes manifest
   */
  private static async loadThemesManifest(): Promise<void> {
    try {
      const response = await fetch('/themes/themes.json');
      if (!response.ok) {
        throw new Error(`Failed to fetch themes.json: ${response.statusText}`);
      }

      this.themesManifest = await response.json();
      log.debug(` Loaded ${this.themesManifest!.themes.length} theme(s) from manifest`);
    } catch (error) {
      log.error(' Failed to load themes manifest:', error);
      throw error;
    }
  }

  /**
   * Get user's selected theme from user-scoped localStorage.
   * Cookie fallback was removed because the cookie is global to the domain
   * and would leak the previous user's theme onto a fresh login on a shared
   * workstation. Cookie is re-synced from localStorage on every read so CMS
   * sees the current EM3 user's theme.
   */
  static getSelectedTheme(): string {
    const key = this.getThemeKey();
    let theme = this.DEFAULT_THEME;
    if (key) {
      const localSaved = localStorage.getItem(key);
      if (localSaved) {
        theme = localSaved;
      }
    }
    // Sync cookie with the value we just resolved so CMS is always in step
    // with whichever user is currently active in EM3.
    setCookie(this.THEME_COOKIE_NAME, theme, 365, '/', 'Lax', getRootDomain());
    return theme;
  }

  /**
   * Get all available themes
   */
  static getAvailableThemes(): ThemeMetadata[] {
    if (!this.themesManifest) {
      log.warn(' Themes manifest not loaded');
      return [];
    }
    return this.themesManifest.themes;
  }

  /**
   * Get theme metadata by ID
   */
  static getThemeById(themeId: string): ThemeMetadata | undefined {
    if (!this.themesManifest) {
      return undefined;
    }
    return this.themesManifest.themes.find(theme => theme.id === themeId);
  }

  /**
   * Load a specific theme
   * @param themeId - Theme identifier (e.g., 'default', 'summer')
   * @param saveSelection - Whether to save the selection to localStorage (default: true)
   */
  static async loadTheme(themeId: string, saveSelection = true): Promise<void> {
    try {
      log.debug(` Loading theme: ${themeId}`);

      // Verify theme exists in manifest
      const theme = this.getThemeById(themeId);
      if (!theme) {
        log.warn(` Theme '${themeId}' not found in manifest, falling back to default`);
        themeId = this.DEFAULT_THEME;
      }

      // Remove currently loaded theme
      this.removeThemeCSS();

      // Load new theme CSS
      // Note: Vite compiles .theme.scss to .theme.css automatically
      const themeFilename = theme?.filename || `${themeId}.theme.scss`;
      const themePath = `/themes/${themeFilename.replace('.scss', '.css')}`;

      await this.loadCSS(themePath, this.THEME_CSS_ID);

      // Save selection if requested
      if (saveSelection) {
        // Save to user-scoped localStorage (primary storage)
        const key = this.getThemeKey();
        if (key) {
          localStorage.setItem(key, themeId);
        }

        // Also save to cookie for cross-app sync (CMS reads it).
        // Cookie is unscoped intentionally; CMS only reads, never writes.
        setCookie(this.THEME_COOKIE_NAME, themeId, 365, '/', 'Lax', getRootDomain());

        log.debug(` Theme '${themeId}' saved to localStorage and cookie`);
      }

      log.debug(` Theme '${themeId}' loaded successfully`);
    } catch (error) {
      log.error(` Failed to load theme '${themeId}':`, error);
      throw error;
    }
  }

  /**
   * Remove currently loaded theme CSS
   */
  private static removeThemeCSS(): void {
    const existing = document.getElementById(this.THEME_CSS_ID);
    if (existing) {
      existing.remove();
      log.debug(' Previous theme CSS removed');
    }
  }

  /**
   * Load a CSS file dynamically
   * @param url - URL of the CSS file
   * @param id - Optional ID for the link element
   */
  private static loadCSS(url: string, id?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      if (id) {
        link.id = id;
      }

      link.onload = () => {
        log.debug(` CSS loaded: ${url}`);
        resolve();
      };

      link.onerror = () => {
        const error = new Error(`Failed to load CSS: ${url}`);
        log.error(error);
        reject(error);
      };

      document.head.appendChild(link);
    });
  }

  /**
   * Check if theme system is initialized
   */
  static isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get current theme ID
   */
  static getCurrentTheme(): string {
    return this.getSelectedTheme();
  }

  /**
   * Reset to default theme
   */
  static async resetToDefault(): Promise<void> {
    await this.loadTheme(this.DEFAULT_THEME);
  }
}
